// src/components/SubmitTime/hooks/useCascadeLoading.ts
// Custom hook for cascade loading of dependent data
// Migrated from SubmitTime.js cascade loading logic

import { useState, useCallback } from 'react';
import type {
  LayerItem,
  PhaseItem,
  ProjectItem,
  TypeItem,
  TaskItem,
  Team,
  TeamResponse,
} from '../types';
import { isRealPhase } from '../types';

export interface CascadeLoadingHelpers {
  // State
  dataLayers: LayerItem[];
  setDataLayers: (layers: LayerItem[]) => void;
  dataPhases: PhaseItem[];
  setDataPhases: (phases: PhaseItem[]) => void;
  dataProjects: ProjectItem[];
  setDataProjects: (projects: ProjectItem[]) => void;
  dataType: TypeItem[];
  setDataType: (types: TypeItem[]) => void;
  dataTasks: TaskItem[];
  setDataTasks: (tasks: TaskItem[]) => void;
  defaultTeams: Team[];
  setDefaultTeams: (teams: Team[]) => void;

  // Selected items
  selectedId: LayerItem | null;
  setSelectedId: (layer: LayerItem | null) => void;
  phaseSelected: PhaseItem | null;
  setPhaseSelected: (phase: PhaseItem | null) => void;
  projectSelected: ProjectItem | null;
  setProjectSelected: (project: ProjectItem | null) => void;
  typeSelected: TypeItem | null;
  setTypeSelected: (type: TypeItem | null) => void;
  taskSelected: TaskItem | null;
  setTaskSelected: (task: TaskItem | null) => void;
  selectedTeam: Team | null;
  setSelectedTeam: (team: Team | null) => void;

  // Loading flags
  isLoadingLayers: boolean;
  isLoadingPhases: boolean;
  isLoadingProjects: boolean;
  isLoadingTypes: boolean;
  isLoadingTasks: boolean;
  isLoadingTeams: boolean;

  // Loading methods
  loadLayers: (api: any, conversationId?: number) => Promise<LayerItem[]>;
  loadAllLayers: (api: any, layerId?: number, date?: string) => Promise<LayerItem[]>;
  loadPhases: (api: any, conversationId: number) => Promise<PhaseItem[]>;
  loadAllProjects: (api: any) => Promise<ProjectItem[]>;
  loadAllTypes: (api: any) => Promise<TypeItem[]>;
  loadTasks: (api: any, configValueId: number, projectId: number) => Promise<TaskItem[]>;
  loadTeams: (api: any) => Promise<TeamResponse>;

  // Reset methods
  resetSelections: () => void;
  resetProjectDependents: () => void;
  resetTypeDependents: () => void;
}

export function useCascadeLoading(): CascadeLoadingHelpers
{
  // State for data lists
  const [dataLayers, setDataLayers] = useState<LayerItem[]>([]);
  const [dataPhases, setDataPhases] = useState<PhaseItem[]>([]);
  const [dataProjects, setDataProjects] = useState<ProjectItem[]>([]);
  const [dataType, setDataType] = useState<TypeItem[]>([]);
  const [dataTasks, setDataTasks] = useState<TaskItem[]>([]);
  const [defaultTeams, setDefaultTeams] = useState<Team[]>([]);

  // State for selected items
  const [selectedId, setSelectedId] = useState<LayerItem | null>(null);
  const [phaseSelected, setPhaseSelected] = useState<PhaseItem | null>(null);
  const [projectSelected, setProjectSelected] = useState<ProjectItem | null>(null);
  const [typeSelected, setTypeSelected] = useState<TypeItem | null>(null);
  const [taskSelected, setTaskSelected] = useState<TaskItem | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);

  // Loading flags
  const [isLoadingLayers, setIsLoadingLayers] = useState(false);
  const [isLoadingPhases, setIsLoadingPhases] = useState(false);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isLoadingTypes, setIsLoadingTypes] = useState(false);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);

  // Load layers for a conversation
  const loadLayers = useCallback(
    async (_api: any, conversationId?: number): Promise<LayerItem[]> =>
    {
      setIsLoadingLayers(true);
      try
      {
        const { submitTimeService } = await import('@/services/submitTimeService');
        const layers = conversationId
          ? await submitTimeService.getLayers(conversationId)
          : await submitTimeService.getAllLayers();

        setDataLayers(layers);
        return layers;
      }
      catch (error)
      {
        console.error('Error loading layers:', error);
        return [];
      }
      finally
      {
        setIsLoadingLayers(false);
      }
    },
    [],
  );

  // Load all layers (for standalone mode)
  const loadAllLayers = useCallback(
    async (_api: any, _layerId?: number, _date?: string): Promise<LayerItem[]> =>
    {
      setIsLoadingLayers(true);
      try
      {
        const { submitTimeService } = await import('@/services/submitTimeService');
        const layers = await submitTimeService.getAllLayers();
        setDataLayers(layers);
        return layers;
      }
      catch (error)
      {
        console.error('Error loading all layers:', error);
        return [];
      }
      finally
      {
        setIsLoadingLayers(false);
      }
    },
    [],
  );

  // Load phases for a conversation
  const loadPhases = useCallback(
    async (_api: any, conversationId: number): Promise<PhaseItem[]> =>
    {
      setIsLoadingPhases(true);
      try
      {
        const { submitTimeService } = await import('@/services/submitTimeService');
        const response = await submitTimeService.getPhasesByConversationId(conversationId);
        const phases = (response || []).filter(isRealPhase);
        setDataPhases(phases);
        return phases;
      }
      catch (error)
      {
        console.error('Error loading phases:', error);
        return [];
      }
      finally
      {
        setIsLoadingPhases(false);
      }
    },
    [],
  );

  // Load all projects
  const loadAllProjects = useCallback(async (_api: any): Promise<ProjectItem[]> =>
  {
    setIsLoadingProjects(true);
    try
    {
      const { submitTimeService } = await import('@/services/submitTimeService');
      const projects = await submitTimeService.getAllProjects();
      setDataProjects(projects);
      return projects;
    }
    catch (error)
    {
      console.error('Error loading projects:', error);
      return [];
    }
    finally
    {
      setIsLoadingProjects(false);
    }
  }, []);

  // Load all types
  const loadAllTypes = useCallback(async (_api: any): Promise<TypeItem[]> =>
  {
    setIsLoadingTypes(true);
    try
    {
      const { submitTimeService } = await import('@/services/submitTimeService');
      const types = await submitTimeService.getAllType();
      setDataType(types);
      return types;
    }
    catch (error)
    {
      console.error('Error loading types:', error);
      return [];
    }
    finally
    {
      setIsLoadingTypes(false);
    }
  }, []);

  // Load tasks for a project and type
  const loadTasks = useCallback(
    async (_api: any, configValueId: number, projectId: number): Promise<TaskItem[]> =>
    {
      setIsLoadingTasks(true);
      try
      {
        const { submitTimeService } = await import('@/services/submitTimeService');
        const tasks = await submitTimeService.getTasks({
          ConfigValueID: configValueId,
          ProjectID: projectId,
        });
        setDataTasks(tasks);
        return tasks;
      }
      catch (error)
      {
        console.error('Error loading tasks:', error);
        return [];
      }
      finally
      {
        setIsLoadingTasks(false);
      }
    },
    [],
  );

  // Load teams - returns TeamResponse and sets defaultTeams + selectedTeam
  const loadTeams = useCallback(async (_api: any): Promise<TeamResponse> =>
  {
    setIsLoadingTeams(true);
    try
    {
      const { submitTimeService } = await import('@/services/submitTimeService');
      const response = await submitTimeService.getTeams();
      setDefaultTeams(response.teams);
      if (response.defaultteam)
      {
        setSelectedTeam(response.defaultteam);
      }
      return response;
    }
    catch (error)
    {
      console.error('Error loading teams:', error);
      return { defaultteam: null, teams: [], submittimeteam: false };
    }
    finally
    {
      setIsLoadingTeams(false);
    }
  }, []);

  // Reset all selections
  const resetSelections = useCallback(() =>
  {
    setSelectedId(null);
    setPhaseSelected(null);
    setProjectSelected(null);
    setTypeSelected(null);
    setTaskSelected(null);
  }, []);

  // Reset project dependents (type selection and task only)
  // Note: dataType is NOT cleared — types are loaded globally and are not project-dependent
  const resetProjectDependents = useCallback(() =>
  {
    setTypeSelected(null);
    setTaskSelected(null);
    setDataTasks([]);
    setPhaseSelected(null);
    setDataPhases([]);
  }, []);

  // Reset type dependents (task and its phases)
  const resetTypeDependents = useCallback(() =>
  {
    setTaskSelected(null);
    setDataTasks([]);
    setPhaseSelected(null);
    setDataPhases([]);
  }, []);

  return {
    // State
    dataLayers,
    setDataLayers,
    dataPhases,
    setDataPhases,
    dataProjects,
    setDataProjects,
    dataType,
    setDataType,
    dataTasks,
    setDataTasks,
    defaultTeams,
    setDefaultTeams,

    // Selected items
    selectedId,
    setSelectedId,
    phaseSelected,
    setPhaseSelected,
    projectSelected,
    setProjectSelected,
    typeSelected,
    setTypeSelected,
    taskSelected,
    setTaskSelected,
    selectedTeam,
    setSelectedTeam,

    // Loading flags
    isLoadingLayers,
    isLoadingPhases,
    isLoadingProjects,
    isLoadingTypes,
    isLoadingTasks,
    isLoadingTeams,

    // Loading methods
    loadLayers,
    loadAllLayers,
    loadPhases,
    loadAllProjects,
    loadAllTypes,
    loadTasks,
    loadTeams,

    // Reset methods
    resetSelections,
    resetProjectDependents,
    resetTypeDependents,
  };
}
